import { useMutation } from './mutation.js';
import { CLE_SESSION } from '../auth/queryClient.js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  DroitAddon,
  ModuleAddon,
  CategorieModule,
  ModeAddon,
  FacturesAddonsGalerie,
  SouscriptionAddonResume,
} from '@artosera/shared';
import { api, requeteTelechargement } from './client.js';
import { ApiError } from './errors.js';
import { modeDemonstrationActif } from './demo.js';
import { appelDeRequete, cleMetier, dansLaPortee, dansLaPorteeCourante } from './cles.js';

export interface AddonRegie extends DroitAddon {
  etat: 'en_cours' | 'echu' | 'lecture_seule';
  masqueLe?: string;
  echeanceProche: boolean;
}
export function useAddons(galerie: string, autorise = true) {
  return useQuery({
    queryKey: cleMetier(galerie, 'addons'),
    queryFn: (contexte) =>
      api.get<{ addons: AddonRegie[]; marketplaceVisible: boolean }>(
        `/g/${encodeURIComponent(galerie)}/reglages/addons`,
        {
          galerie,
          ...appelDeRequete(contexte),
        },
      ),
    enabled: autorise,
    refetchInterval: 30_000,
    // Une publication retirée doit disparaître sans conserver la dernière liste.
    staleTime: 0,
  });
}

export interface AddonCatalogue {
  addon: ModuleAddon;
  categorie: CategorieModule;
  mensuelHtCentimes?: number;
  annuelHtCentimes?: number;
  achatHtCentimes?: number;
  versionPrix: number;
  essaiJours: number;
  dependanceManquante?: 'facturation';
  boiteReception?: boolean;
  etat: 'actif' | 'echu' | 'disponible' | 'lecture_seule' | 'deja_essaye';
  masqueLe?: string;
  dejaEssaye?: boolean;
  mode?: ModeAddon;
  jusquAu?: string | null;
}
export interface CatalogueAddons {
  categories: { categorie: CategorieModule; addons: AddonCatalogue[] }[];
}
export function useCatalogueAddons(galerie: string, autorise: boolean) {
  return useQuery({
    queryKey: cleMetier(galerie, 'addons', 'catalogue'),
    queryFn: (contexte): Promise<CatalogueAddons> =>
      modeDemonstrationActif()
        ? Promise.resolve({
            categories: [
              {
                categorie: 'ventes-clients',
                addons: [
                  {
                    addon: 'pipeline-ventes',
                    categorie: 'ventes-clients',
                    mensuelHtCentimes: 1250,
                    annuelHtCentimes: 12500,
                    versionPrix: 1,
                    essaiJours: 30,
                    etat: 'disponible',
                  },
                ],
              },
            ],
          })
        : api.get(`/g/${encodeURIComponent(galerie)}/reglages/addons/catalogue`, {
            galerie,
            ...appelDeRequete(contexte),
          }),
    enabled: autorise,
    refetchInterval: 30_000,
    staleTime: 0,
  });
}

export function useCommanderEssai(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      retry: false,
      mutationFn: (
        saisie: {
          addon: ModuleAddon;
          cle: string;
          avecDependances: boolean;
          versionsPrix: Record<string, number>;
        },
        appel,
      ) =>
        api.post(
          `/g/${encodeURIComponent(galerie)}/reglages/addons/commandes`,
          {
            addon: saisie.addon,
            nature: 'essai',
            avecDependances: saisie.avecDependances,
            versionsPrix: saisie.versionsPrix,
          },
          { galerie, ...appel, cleOperation: saisie.cle },
        ),
      onError: async (erreur) => {
        if (
          erreur instanceof ApiError &&
          erreur.status === 409 &&
          (erreur.details as { raison?: string } | null)?.raison === 'prix_modifie'
        ) {
          await client.invalidateQueries({ queryKey: cleMetier(galerie, 'addons', 'catalogue') });
        }
      },
      onSuccess: async () => {
        await Promise.all([
          client.invalidateQueries({ queryKey: cleMetier(galerie, 'addons') }),
          client.invalidateQueries({ queryKey: CLE_SESSION }),
        ]);
      },
    }),
  );
}

export type NaturePayante = 'location_mensuelle' | 'location_annuelle' | 'achat';
export interface DevisAddon {
  empreinteDevis: string;
  lignes: {
    addon: ModuleAddon;
    formule: string;
    depuisLe: string;
    jusquAu: string | null;
    htBrutCentimes: number;
    remiseCentimes: number;
    htNetCentimes: number;
  }[];
  tva: { categorie: string; taux: number; mention?: string | null };
  totaux: { totalLignesCentimes: number; totalTvaCentimes: number; totalTtcCentimes: number };
  versionPrix: Record<string, number>;
  conditionsVersion: string;
}
export function useDevisAddon(
  galerie: string,
  addon: ModuleAddon,
  nature: NaturePayante,
  avecDependances: boolean,
) {
  return useQuery({
    queryKey: cleMetier(galerie, 'addons', 'devis', addon, nature, String(avecDependances)),
    queryFn: (contexte) =>
      api.get<DevisAddon>(
        `/g/${encodeURIComponent(galerie)}/reglages/addons/devis?${new URLSearchParams({ addon, nature, avecDependances: String(avecDependances) })}`,
        { galerie, ...appelDeRequete(contexte) },
      ),
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}
export function useCommanderPayant(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      retry: false,
      mutationFn: (
        saisie: {
          addon: ModuleAddon;
          nature: NaturePayante;
          avecDependances: boolean;
          cle: string;
          versionsPrix: Record<string, number>;
          conditionsVersion: string;
          empreinteDevis: string;
        },
        appel,
      ) => {
        const { cle, ...corps } = saisie;
        return api.post(`/g/${encodeURIComponent(galerie)}/reglages/addons/commandes`, corps, {
          galerie,
          ...appel,
          cleOperation: cle,
        });
      },
      onSuccess: async () => {
        await Promise.all([
          client.invalidateQueries({ queryKey: cleMetier(galerie, 'addons') }),
          client.invalidateQueries({ queryKey: CLE_SESSION }),
        ]);
      },
    }),
  );
}

export function useFacturesAddons(galerie: string, autorise: boolean) {
  return useQuery({
    queryKey: cleMetier(galerie, 'addons', 'factures'),
    queryFn: (contexte): Promise<FacturesAddonsGalerie & { marketplaceVisible: boolean }> =>
      modeDemonstrationActif()
        ? Promise.resolve({
            marketplaceVisible: true,
            paiementEnLigneDisponible: false,
            items: [],
            virement: { iban: null, bic: null, titulaire: null },
          })
        : api.get(`/g/${encodeURIComponent(galerie)}/reglages/addons/factures`, {
            galerie,
            ...appelDeRequete(contexte),
          }),
    enabled: autorise,
    staleTime: 0,
  });
}

export function telechargerFactureAddon(galerie: string, id: string) {
  return dansLaPorteeCourante((appel) =>
    requeteTelechargement(
      `/g/${encodeURIComponent(galerie)}/reglages/addons/factures/${encodeURIComponent(id)}/pdf`,
      { galerie, ...appel },
    ),
  );
}

export function useSouscriptionsAddons(galerie: string, autorise: boolean) {
  return useQuery({
    queryKey: cleMetier(galerie, 'addons', 'souscriptions'),
    queryFn: (contexte) =>
      api.get<{ items: SouscriptionAddonResume[] }>(
        `/g/${encodeURIComponent(galerie)}/reglages/addons/souscriptions`,
        { galerie, ...appelDeRequete(contexte) },
      ),
    enabled: autorise && !modeDemonstrationActif(),
  });
}

export function useResiliationAddon(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      retry: false,
      mutationFn: async (
        {
          souscription,
          geste,
        }: {
          souscription: SouscriptionAddonResume;
          geste: 'resilier' | 'annuler-resiliation';
        },
        appel,
      ) => {
        if (modeDemonstrationActif())
          throw new ApiError(403, 'acces.refuse', 'Indisponible en démonstration.');
        const fraiches = await api.get<{
          items: SouscriptionAddonResume[];
        }>(`/g/${encodeURIComponent(galerie)}/reglages/addons/souscriptions`, {
          galerie,
          ...appel,
        });
        if (
          fraiches.items.find((s) => s.id === souscription.id)?.revision !== souscription.revision
        )
          throw new ApiError(409, 'conflit.version', 'La souscription a changé.', {
            raison: 'revision_souscription_perimee',
          });
        return api.post(
          `/g/${encodeURIComponent(galerie)}/reglages/addons/souscriptions/${souscription.id}/${geste}`,
          { revision: souscription.revision },
          { galerie, ...appel },
        );
      },
      onSettled: async () => {
        await client.invalidateQueries({ queryKey: cleMetier(galerie, 'addons') });
      },
    }),
  );
}

export function usePaiementAddon(galerie: string) {
  return useMutation(
    dansLaPortee({
      retry: false,
      mutationFn: ({ factureId, cle }: { factureId: string; cle: string }, appel) => {
        if (modeDemonstrationActif())
          throw new ApiError(403, 'acces.refuse', 'Indisponible en démonstration.');
        return api.post<{ id: string; url: string }>(
          `/g/${encodeURIComponent(galerie)}/reglages/addons/factures/${factureId}/paiement`,
          {},
          { galerie, ...appel, cleOperation: cle },
        );
      },
    }),
  );
}
export function useSimulationPaiementAddon(galerie: string) {
  return useMutation(
    dansLaPortee({
      retry: false,
      mutationFn: (
        {
          factureId,
          tentativeId,
          issue,
        }: { factureId: string; tentativeId: string; issue: 'payee' | 'echouee' | 'abandonnee' },
        appel,
      ) =>
        api.post<{ url: string }>(
          `/g/${encodeURIComponent(galerie)}/reglages/addons/factures/${factureId}/paiement/${tentativeId}/simuler`,
          { issue },
          { galerie, ...appel },
        ),
    }),
  );
}
